import { Analytics } from '@cobliteam/analytics';
import {
  ActivityCompletionStatus,
  ActivityVisitStatus,
  type DeviceTrip,
  type EditActivity,
  type EditRoute,
  GeocoderAddressComponentTypes,
  type NewActivity,
  NewActivityType,
  type NewDestination,
  type NewRoute,
  type ParsedSpreadsheetTab,
  RouteStatus,
} from '@cobliteam/api-lib';
import { addressComponentConcat } from '@cobliteam/api-lib/src/dashboard/routing/utils';
import { type GoogleGeocoderAddressComponent, type GoogleLatLng, getLicensePlateOrCobliIdOrPlaceholder, type TimestampInMillis, type Vehicle } from '@cobliteam/common-types';
import type { TFunction } from '@cobliteam/i18n';
import { Environment } from '@cobliteam/react-environments';
import { colors } from '@cobliteam/ui/theme';
import type { SemanticKey } from '@cobliteam/ui/theme/tokens/variants/sharedVariants';
import type { LatLngExpression, PointExpression } from 'leaflet';
import moment from 'moment';
import type { ParseSpreadsheetRequest } from './store/Routing/actions';
import { FileSizeExceededError, InvalidMimeTypeError } from './store/Routing/errors';

export const MAX_ROUTE_NAME_CHARS = 100;
export const MAX_ACTIVITY_NAME_CHARS = 100;
export const MAX_ACTIVITY_ADDITIONAL_INFO_CHARS = 1000;
export const DEFAULT_DEBOUNCE_DELAY = 500;
export const DEFAULT_ZOOM = 12;
export const DEFAULT_CENTER: LatLngExpression = [-23.6821604, -46.8754856];
export const FIT_BOUND_PADDING: PointExpression = [100, 100];
export const VALID_HOUR_REGEX = /[0-9][0-9]:[0-9][0-9]/;
export const DEFAULT_RADIX = 10;
export const ATLAS_ROUTE_DISPATCH_WEB_URL = 'https://rota-v2.cobli.co';
export const SPREADSHEET_ROUTING_MODEL_URL = 'https://new-routing-report-templates.s3.amazonaws.com/planilha_modelo_roteirizac%CC%A7a%CC%83o.xlsx';
export const SPREADSHEET_ACTIVITIES_MODEL_URL = 'https://new-routing-report-templates.s3.amazonaws.com/planilha_modelo_atividades.xlsx';

const env = Environment.getInstance().getEnvironment();

const VALID_SPREADSHEET_MIME_TYPES = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];

const ONE_MEGA_BYTE_IN_BYTES = 1048576;

export enum Months {
  janeiro = 0,
  fevereiro = 1,
  março = 2,
  abril = 3,
  maio = 4,
  junho = 5,
  julho = 6,
  agosto = 7,
  setembro = 8,
  outubro = 9,
  novembro = 10,
  dezembro = 11,
}

export const SERVICES_AND_DELIVERIES_LOCAL_STORAGE_KEY = 'servicesAndDeliveries';

export interface ServicesAndDeliveriesLocalStorage {
  haveEverCreatedARoute: boolean;
  hideRoutingOptionsInfoBanner: boolean;
  hideActivitiesListInfoBanner: boolean;
  hideRoutingOptimizationInfoBanner: boolean;
  hideDriverAppBanner: boolean;
  hideControlTowerModal: boolean;
}

interface TrackingProperties {
  [key: string]: any;
}

const padHourData = (num: number) => num.toString().padStart(2, '0');

export const isEmptyOrWhitespace = (sentence: string) => !/\S/.test(sentence);

export const millisToDate = (timestampInMillis: TimestampInMillis = 0) => moment(timestampInMillis).format('DD/MM/YYYY');

export const hourToMillis = (hour: string, startDate?: TimestampInMillis) => {
  const date = moment(startDate).format('DD/MM/YYYY');
  const dateInMillis = moment(`${date} ${hour}`, 'DD/MM/YYYY hh:mm').valueOf();

  return dateInMillis;
};

export const millisToHour = (timestampInMillis?: TimestampInMillis) => {
  if (timestampInMillis !== undefined) {
    const duration = moment(timestampInMillis);

    return `${padHourData(duration.hours())}:${padHourData(duration.minutes())}`;
  }

  return '--:--';
};

export const millisToMinutes = (timestampInMillis: TimestampInMillis = 0) => moment.duration(timestampInMillis).asMinutes().toFixed(0);

export const minutesToMillis = (minutes: string) => moment.duration(Number.parseInt(minutes, DEFAULT_RADIX), 'minutes').asMilliseconds();

export const formatDuration = (duration: TimestampInMillis) => {
  const momentDuration = moment.duration(duration);
  const hours = momentDuration.days() * 24 + momentDuration.hours();
  const hoursString = hours > 0 ? `${hours}h` : '';
  const minutes = momentDuration.seconds() > 1 ? momentDuration.minutes() + 1 : momentDuration.minutes();
  const minutesString = minutes > 9 ? minutes : `0${minutes}`;

  return `${hoursString}${minutesString}min`;
};

export const formatDistance = (distanceInMeters: number) => {
  const distanceInKm = distanceInMeters / 1000;

  return `${distanceInKm.toFixed(2)}km`;
};

export const getRandomInt = (max: number) => Math.floor(Math.random() * (max + 1));

export const activityStatusLabel = (t: TFunction, activityVisitStatus?: ActivityVisitStatus) => {
  switch (activityVisitStatus) {
    case ActivityVisitStatus.VISITED:
      return t('activity_status_label.visited');
    case ActivityVisitStatus.ON_SITE:
      return t('activity_status_label.on_site');
    case ActivityVisitStatus.NOT_VISITED:
      return t('activity_status_label.not_visited');
    default:
      return t('activity_status_label.pending');
  }
};

interface ActivityContent {
  label: string;
  color: SemanticKey;
}

export const activityContent = (t: TFunction, activityVisitStatus?: ActivityVisitStatus, isFisrtActivity?: boolean): ActivityContent => {
  switch (activityVisitStatus) {
    case ActivityVisitStatus.VISITED:
      return {
        label: isFisrtActivity ? t('activity_content.route_started') : activityStatusLabel(t, activityVisitStatus),
        color: 'success',
      };
    case ActivityVisitStatus.ON_SITE:
      return {
        label: activityStatusLabel(t, activityVisitStatus),
        color: 'primary',
      };
    case ActivityVisitStatus.NOT_VISITED:
      return {
        label: activityStatusLabel(t, activityVisitStatus),
        color: 'warning',
      };
    default:
      return {
        label: activityStatusLabel(t, activityVisitStatus),
        color: 'default',
      };
  }
};

export const summarySubtitle = (t: TFunction, visitStatus: ActivityVisitStatus, accordionExpanded: boolean) => {
  if (accordionExpanded) {
    return activityStatusLabel(t, visitStatus);
  }
};

export const mapAddressComponents = (addressComponents: GoogleGeocoderAddressComponent[]) =>
  Object.values(GeocoderAddressComponentTypes).reduce((result: Partial<Record<GeocoderAddressComponentTypes, string>>, value: GeocoderAddressComponentTypes) => {
    const addressComponent = addressComponents.find((component) => component.types[0] === value);

    if (addressComponent) {
      result[value] = addressComponent.short_name;
    }

    return result;
  }, {});

export const mapProposedDestination = (location: GoogleLatLng, addressComponents: GoogleGeocoderAddressComponent[]) => {
  const {
    administrative_area_level_2: city,
    country,
    subpremise: streetComplement,
    sublocality_level_1: neighborhood,
    postal_code: postalCode,
    administrative_area_level_1: state,
    route: streetAddress,
    street_number: streetNumber,
  } = mapAddressComponents(addressComponents);

  return {
    coordinate: {
      latitude: location.lat(),
      longitude: location.lng(),
    },

    ...(city && { city }),
    ...(country && { country }),
    ...(neighborhood && { neighborhood }),
    ...(postalCode && { postalCode }),
    ...(state && { state }),
    ...(streetAddress && { streetAddress }),
    ...(streetComplement && { streetComplement }),
    ...(streetNumber && { streetNumber }),
  };
};

export const getActivityAddressComponents = (activity: NewActivity) => {
  const { city, state, streetAddress, streetNumber, neighborhood, postalCode } = activity.destination;

  const streetAndNumber = addressComponentConcat(', ', streetAddress, streetNumber);
  const neighborhoodAndCity = addressComponentConcat(', ', neighborhood, city);
  const stateAndPostalCode = addressComponentConcat(', ', state, postalCode);

  return [streetAndNumber, neighborhoodAndCity, stateAndPostalCode];
};

export const getDestinationAddressComponents = (destination: NewDestination) => {
  const { city, state, streetAddress, streetNumber, neighborhood, postalCode } = destination;

  const streetAndNumber = addressComponentConcat(', ', streetAddress, streetNumber);
  const neighborhoodAndCity = addressComponentConcat(', ', neighborhood, city);
  const stateAndPostalCode = addressComponentConcat(', ', state, postalCode);

  return [streetAndNumber, neighborhoodAndCity, stateAndPostalCode];
};

export const getFormattedAddress = (destination: NewDestination) =>
  getDestinationAddressComponents(destination)
    .filter((component) => component && !isEmptyOrWhitespace(component))
    .join(' - ');

export const isValidAddressComponent = (summaryTitle?: string) => (component?: string) => component !== undefined && !isEmptyOrWhitespace(component) && component !== summaryTitle;

export const compareActivitiesByArrivalTime = (activity1: NewActivity, activity2: NewActivity) => {
  const arrivalTime1 = activity1.arrivalTime;
  const arrivalTime2 = activity2.arrivalTime;

  if (arrivalTime1 && arrivalTime2) {
    return arrivalTime1 > arrivalTime2 ? 1 : -1;
  }

  return 0;
};

export const getRouteDuration = (route: NewRoute) => {
  const { activities } = route;

  const completedActivities = activities.filter((activity) => activity.visitStatus === ActivityVisitStatus.VISITED).sort(compareActivitiesByArrivalTime);

  const startActivityArrivalTime = completedActivities[0]?.arrivalTime;
  const endActivityArrivalTime = completedActivities[completedActivities.length - 1] && completedActivities[completedActivities.length - 1].arrivalTime;

  if (startActivityArrivalTime && endActivityArrivalTime) {
    return endActivityArrivalTime - startActivityArrivalTime;
  }

  return null;
};

export const getLoadRouteExecutionInfoRequestObject = (route?: NewRoute, vehicle?: Vehicle) => {
  if (route && vehicle) {
    const { status, activities } = route;
    const { deviceId } = vehicle;

    const completedActivities = activities.filter((activity) => activity.visitStatus === ActivityVisitStatus.VISITED).sort(compareActivitiesByArrivalTime);

    const startActivityArrivalTime = completedActivities[0]?.arrivalTime;
    const endActivityArrivalTime = completedActivities[completedActivities.length - 1] && completedActivities[completedActivities.length - 1].arrivalTime;

    if (status === RouteStatus.EXECUTED && startActivityArrivalTime && endActivityArrivalTime && completedActivities.length > 1) {
      return {
        esn: deviceId,
        startActivityArrivalTime,
        endActivityArrivalTime,
      };
    }
  }
  return null;
};

export const calculateDrivenDistanceInMeters = (deviceTrip: DeviceTrip) => {
  const { pathGeojson } = deviceTrip;

  const filteredDistances = pathGeojson.properties.filter(({ accumulatedDistance }) => accumulatedDistance !== undefined && accumulatedDistance !== null);

  if (filteredDistances.length === 0) {
    return 0;
  }

  const distanceDrivenStart = filteredDistances[0].accumulatedDistance || 0;
  const distanceDrivenEnd = filteredDistances[filteredDistances.length - 1].accumulatedDistance || 0;

  return Math.abs(distanceDrivenEnd - distanceDrivenStart);
};

export const logSwitchStateChange = (eventName: string, currentState: boolean, aditinalProps?: TrackingProperties) =>
  Analytics.track(eventName, {
    action: currentState ? 'deactivate' : 'activate',
    ...aditinalProps,
  });

export const getTabContext = (t: TFunction) => ({
  ACTIVITIES: t('tab_context.activities'),
  ROUTES: t('tab_context.routes'),
});

export const getVehicleAllocationType = (prevVehicle?: Vehicle, currVehicle?: Vehicle) => {
  if (!prevVehicle && currVehicle) {
    return 'allocation';
  }

  if (prevVehicle && !currVehicle) {
    return 'deallocation';
  }

  return 'swap';
};

const UNEDITABLE_ROUTE_STATUSES = [RouteStatus.EXECUTED, RouteStatus.UNEXECUTED, RouteStatus.PARTIALLY_EXECUTED];

export const isRouteEditingDisabled = (route?: NewRoute) => {
  if (!route) return false;

  const { status } = route;
  return UNEDITABLE_ROUTE_STATUSES.includes(status);
};

export const editRouteButtonPopupTitle = (t: TFunction, routeStatus?: RouteStatus) =>
  routeStatus === RouteStatus.EXECUTED || routeStatus === RouteStatus.UNEXECUTED
    ? t('edit_route_button_popup_title.route_ended')
    : routeStatus === RouteStatus.DELAYED
      ? t('edit_route_button_popup_title.cannot_edit')
      : t('edit_route_button_popup_title.route_started');

export const getActivityEditDisabledByStatusText = (t: TFunction, activityCompletionStatus: ActivityCompletionStatus) => {
  switch (activityCompletionStatus) {
    case ActivityCompletionStatus.COMPLETED:
      return t('activity_edit_disabled_by_status.completed');
    default:
      return t('activity_edit_disabled_by_status.not_completed');
  }
};

export const getActivityEditDisabledText = (t: TFunction, activity?: NewActivity) => {
  if (activity?.routeId) {
    return t('activity_edit_disabled.belongs_to_route');
  }
  if (activity?.completionStatus) {
    return getActivityEditDisabledByStatusText(t, activity.completionStatus);
  }

  return undefined;
};

export const getActivityDeleteDisabledText = (t: TFunction, activity?: NewActivity) => {
  if (activity?.completionStatus) {
    switch (activity.completionStatus) {
      case ActivityCompletionStatus.COMPLETED:
        return t('activity_delete_disabled.completed');
      default:
        return t('activity_delete_disabled.not_completed');
    }
  }

  return undefined;
};

export const getVehicleItemLabel = (t: TFunction, vehicle: Vehicle) => {
  const vehicleLabel = getLicensePlateOrCobliIdOrPlaceholder(vehicle);
  const driverName = vehicle.driver?.name || t('vehicle_item_label.no_driver_assigned');
  return `${vehicleLabel} - ${driverName}`;
};

export const getDataFromSpreadsheet = async (file: ParseSpreadsheetRequest): Promise<ParsedSpreadsheetTab[]> => {
  if (!VALID_SPREADSHEET_MIME_TYPES.includes(file.payload.type)) {
    throw new InvalidMimeTypeError();
  }
  if (file.payload.size > ONE_MEGA_BYTE_IN_BYTES) {
    throw new FileSizeExceededError();
  }

  const buffer = await file.payload.arrayBuffer();

  // dynamic import
  const xlsx = await import('node-xlsx');
  return xlsx.parse(buffer, {
    type: 'string',
    raw: false,
  }) as ParsedSpreadsheetTab[];
};

export const scrollToFirstActivityError = () => {
  const firstActivityError: HTMLElement | null = document.querySelector('.border-danger') || document.querySelector('.text-danger');
  if (firstActivityError) {
    firstActivityError.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }
};

export const scrollToFirstAddressWithoutStreetNumber = () => {
  const firstAddressWithoutStreetNumber: HTMLElement | null = document.querySelector('div[data-street-number-warning="true"]');
  if (firstAddressWithoutStreetNumber) {
    firstAddressWithoutStreetNumber.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }
};

export const scrollToInputedActivity = (index: number) => {
  const clickedMarker: HTMLElement | null = document.getElementById(`activityItem-${index}`);
  if (clickedMarker) {
    clickedMarker.style.background = colors.accessory.blue[1];
    clickedMarker.scrollIntoView({ behavior: 'smooth', block: 'center' });

    setTimeout(() => {
      clickedMarker.style.background = colors.white.DEFAULT;
    }, 2000);
  }
};

export const getActivityPopupNameAddress = (activity: NewActivity) => {
  const addressComponents = getActivityAddressComponents(activity);
  const popupHeaderTitle = (activity.name ? activity.name : addressComponents.find((component) => component !== undefined)) || '';
  const bodyFormatedAddress = addressComponents.filter(isValidAddressComponent(popupHeaderTitle)).join(' - ');

  return { popupHeaderTitle, bodyFormatedAddress };
};

export const getEditAddressPopupNameAddress = (activity: EditActivity) => {
  const firstAddressComponent = activity.destination?.address_components ? activity.destination.address_components[0]?.long_name : '';
  const popupHeaderTitle = activity.name ? activity.name : firstAddressComponent;

  return {
    popupHeaderTitle,
    bodyFormatedAddress: activity.destination.formatted_address || '',
  };
};

export const getVehicle = (vehicleId: string, vehiclesData: Vehicle[]) => {
  return vehiclesData.find(({ id }) => id === vehicleId[0]);
};

export const generateWhatsAppUrl = (address: string) => `${env.whatsAppApiUrl}text=${address}`;
export const copyAddressToClipboard = (address: string) => {
  const textArea = document.createElement('textarea');
  textArea.value = address;
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('Copy');
  textArea.remove();
};

export const endsOnStarts = (activities: NewActivity[]) => {
  const startPlace = activities.find((activity) => activity.type === NewActivityType.START);
  const endPlace = activities.find((activity) => activity.type === NewActivityType.END);

  if (endPlace && startPlace) {
    return (
      endPlace.destination.coordinate.latitude === startPlace.destination.coordinate.latitude &&
      endPlace.destination.coordinate.longitude === startPlace.destination.coordinate.longitude
    );
  }

  return false;
};

export const editRouteEndsOnStart = (route: EditRoute) => {
  const { activities } = route;
  const startPlace = activities.find((activity) => activity.type === NewActivityType.START);
  const endPlace = activities.find((activity) => activity.type === NewActivityType.END);

  if (endPlace && startPlace) {
    return (
      endPlace.destination.geometry?.location?.lat() === startPlace.destination.geometry?.location?.lat() &&
      endPlace.destination.geometry?.location?.lng() === startPlace.destination.geometry?.location?.lng()
    );
  }

  return false;
};

export const changeRouteVehicle = (route: NewRoute, vehicle?: Vehicle): NewRoute => ({
  ...route,
  driverId: vehicle?.driver?.id,
  driverName: vehicle?.driver?.name,
  deviceId: vehicle?.deviceId,
  vehicleId: vehicle?.id,
});
